'use client';

import { useState } from 'react';

import {
  CURRENCY, PERIOD, priceFor, saleFor, yearlySaving, yearlySavingPercent,
} from './account/plan';
import { usePlans } from './account/usePlans';
import * as EN from './content';
import { FullRun, HeroReel, LoopClip } from './media';
import { PHOTOS, SCREENSHOTS } from './photos';
import {
  trackBilling, trackCta, trackFaq, trackFeature, trackLink, trackRung, useSectionViews,
} from './analytics';
import {
  accentA, DARK_VARS, Eyebrow, Footer, h2Style, MONO, Nav, ruleTop, Shell, TrialCta, wrap,
} from './ui';

// A "→ read more" link out of the page. Rendered only when the destination
// exists, so a page that hasn't been written yet never becomes a dead link.
export function MoreLink({ link, location, style }) {
  if (!link?.href) return null;
  return (
    <a
      href={link.href}
      onClick={() => trackLink(location, link.href)}
      style={{
        color: 'var(--coil)', textDecoration: 'none', fontWeight: 500, ...style,
      }}
    >
      {`→ ${link.more}`}
    </a>
  );
}

// Drawn in TIA Portal's monitoring colours: powered in solid green, unpowered in
// dashed blue, operands above the elements. One deliberate simplification: TIA
// greens the stretch from the rail up to an open contact, which on a landing
// page read as "half on". Here the whole rung is either off or on.
// No glow, no animated flow — the portal has neither.
function LadderRung({ energized }) {
  const on = '#1FA34A';
  const off = '#3A6FD8';
  const rail = '#2B2F33';
  const after = energized ? on : off;
  const dash = energized ? undefined : '5 4';
  const label = { fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fill: '#2B2F33' };
  const sym = { fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fill: '#6E747B' };

  // Coil geometry: two arcs of radius R through (x, 75 ± H). Each bulges
  // outwards by R − √(R² − H²), and the wires stop exactly at the bulge — before
  // this they ran to the arc ends and poked into the coil.
  const R = 26;
  const H = 20;
  const bulge = R - Math.sqrt(R * R - H * H);
  const coilL = 286;
  const coilR = 314;

  return (
    <svg viewBox="0 0 440 150" width="100%" style={{ display: 'block', overflow: 'visible' }}>
      <line x1="30" y1="20" x2="30" y2="130" stroke={rail} strokeWidth="3" />
      <line x1="410" y1="20" x2="410" y2="130" stroke={rail} strokeWidth="3" />
      <line x1="31.5" y1="75" x2="135" y2="75" stroke={after} strokeWidth="2.4" strokeDasharray={dash} />
      <line x1="136.3" y1="55" x2="136.3" y2="95" stroke={after} strokeWidth="2.6" />
      <line x1="156.7" y1="55" x2="156.7" y2="95" stroke={after} strokeWidth="2.6" />
      <line x1="158" y1="75" x2={coilL - bulge} y2="75" stroke={after} strokeWidth="2.4" strokeDasharray={dash} />
      <path d={`M${coilL} ${75 - H} A ${R} ${R} 0 0 0 ${coilL} ${75 + H}`} fill="none" stroke={after} strokeWidth="2.6" />
      <path d={`M${coilR} ${75 - H} A ${R} ${R} 0 0 1 ${coilR} ${75 + H}`} fill="none" stroke={after} strokeWidth="2.6" />
      <line x1={coilR + bulge} y1="75" x2="408.5" y2="75" stroke={after} strokeWidth="2.4" strokeDasharray={dash} />
      <text x="146.5" y="28" textAnchor="middle" style={sym}>&quot;Start&quot;</text>
      <text x="146.5" y="45" textAnchor="middle" style={label}>%I0.0</text>
      <text x="300" y="28" textAnchor="middle" style={sym}>&quot;Motor_On&quot;</text>
      <text x="300" y="45" textAnchor="middle" style={label}>%Q0.0</text>
    </svg>
  );
}

// `copy` is the whole page's text in one language. The German route passes its
// own bundle with the same exports, so /coilmind and /de/coilmind are one
// layout and cannot drift apart.
export default function Landing({ copy = EN, lang = 'en', alt = '/de/coilmind' } = {}) {
  const {
    BILLING_NOTE, BRAND_CHIP, BRAND_STATS, CAPABILITIES_LINK, CHECKOUT_NOTE, CLIPS, COMPAT,
    DECIDE, DIFFERENTIATORS, DIFF_CHIPS, DIFF_LEAD, FAQS, FEATURES, FEATURES_LEAD, FOOTER,
    FOUNDER, FULL_RUN_CHAPTERS, HERO, LANGUAGE_LINE, LICENCE_NOTE, PAINS, PRICING_NOTE, PROOF,
    SAFETY_BULLETS, SAFETY_LEAD, STEPS, TOUR, TRIAL, UI, VIDEO, WHO, WHO_CHIPS,
  } = copy;
  const [energized, setEnergized] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [billing, setBilling] = useState('month');
  const yearly = billing === 'year';
  // Live prices, discounts and the annual saving — all computed server-side.
  // Falls back to the catalogue numbers if the call fails, never to an empty card.
  const { plans } = usePlans();
  // Carry the chosen period into checkout, alongside the intent. The account
  // page normalises anything unrecognised back to monthly, so a stale or
  // hand-typed link can never start a subscription nobody chose.
  const trialHref = yearly
    ? '/coilmind/account?intent=trial&billing=year'
    : '/coilmind/account?intent=trial';

  useSectionViews();

  // The rung toggles on a click or a key, nothing more. The scripted "ghost
  // cursor" that used to click it on first view was a demo-reel trick.
  const toggleRung = () => {
    // Track outside the updater: React may invoke a state updater more than once
    // for the same click, which reported every toggle twice.
    const next = !energized;
    setEnergized(next);
    trackRung(next);
  };

  const h2 = h2Style;

  return (
    <Shell lang={lang}>
      <>
        <Nav lang={lang} alt={alt} />

        {/* ===== HERO ===== */}
        <header id="top" data-cm-section="hero" style={{ position: 'relative', padding: '136px 0 84px' }}>
          <div className="cm-graph" aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
          <div
            className="cm-hero cm-pad"
            style={{
              ...wrap, position: 'relative', display: 'grid', gridTemplateColumns: '1.05fr .95fr', gap: 54, alignItems: 'center',
            }}
          >
            <div>
              <div style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 12px', marginBottom: 14,
              }}
              >
                <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--coil)' }}>{HERO.slogan}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink3)' }}>{HERO.eyebrow}</span>
              </div>
              <p style={{
                fontSize: 15.5, lineHeight: 1.55, color: 'var(--ink3)', margin: '0 0 20px', maxWidth: 520,
              }}
              >
                {HERO.kicker}
              </p>
              <h1 style={{
                fontSize: 'clamp(32px,3.9vw,48px)', fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.1, margin: '0 0 22px', textWrap: 'balance',
              }}
              >
                {HERO.h1}
                {' '}
                {HERO.h1Accent}
              </h1>
              <p style={{
                fontSize: 18.5, lineHeight: 1.62, color: 'var(--ink2)', margin: '0 0 32px', maxWidth: 540,
              }}
              >
                {HERO.sub}
              </p>
              <p style={{
                fontSize: 15.5, lineHeight: 1.55, color: 'var(--ink2)', margin: '-18px 0 30px', maxWidth: 540,
              }}
              >
                {`${HERO.proof} `}
                <a
                  href="#proof"
                  onClick={() => trackCta('hero', HERO.proofLink)}
                  style={{
                    color: 'var(--coil)', textDecoration: 'none', fontSize: 14,
                  }}
                >
                  {HERO.proofLink}
                  {' →'}
                </a>
              </p>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center',
              }}
              >
                <TrialCta
                  location="hero"
                  label={TRIAL.cta}
                  style={{
                    fontSize: 16, padding: '14px 24px', borderRadius: 6,
                  }}
                />
                <a
                  href="#tour"
                  onClick={() => trackCta('hero', 'Watch the 70-second tour')}
                  style={{
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 9, color: 'var(--ink)', fontWeight: 500, fontSize: 16, padding: '14px 20px', borderRadius: 6, border: '1px solid var(--line2)', background: 'var(--panel)',
                  }}
                >
                  {UI.tourCta}
                  {' '}
                  <span style={{ color: 'var(--coil)' }}>→</span>
                </a>
              </div>
              <div style={{
                fontSize: 13, color: 'var(--ink3)', marginTop: 14,
              }}
              >
                {TRIAL.micro}
              </div>
              {/* Spec strip — answers "will this even run on my setup" with facts,
                  and closes on the parent brand rather than a footnote. */}
              <p style={{
                marginTop: 30, marginBottom: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--ink3)', maxWidth: 540,
              }}
              >
                {[...COMPAT, BRAND_CHIP].join(' · ')}
              </p>
            </div>

            {/* interactive ladder demo, framed as a block editor window */}
            <div style={{
              background: 'var(--panel)', border: '1px solid var(--line2)', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 2px rgba(23,25,28,.06), 0 24px 48px -24px rgba(23,25,28,.28)',
            }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: '#2A2F35', color: '#D9DDE2', fontSize: 12.5,
              }}
              >
                <span style={{ fontFamily: MONO, fontSize: 12 }}>
                  PLC_1 › Program blocks › Main [OB1]
                </span>
              </div>
              <div style={{ padding: 18 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6,
                }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                    {UI.rungNetwork}
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: energized ? 'var(--coil)' : 'var(--ink3)',
                  }}
                  >
                    <span style={{
                      width: 7, height: 7, borderRadius: '50%', background: energized ? 'var(--coil)' : 'var(--line2)',
                    }}
                    />
                    {UI.rungMonitoring}
                  </span>
                </div>
                <div
                  onClick={toggleRung}
                  role="button"
                  tabIndex={0}
                  title={UI.rungTitle}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRung(); } }}
                  style={{
                    position: 'relative', cursor: 'pointer', background: '#FFFFFF', border: '1px solid var(--line)', borderRadius: 4, padding: '18px 14px 8px', userSelect: 'none',
                  }}
                >
                  <LadderRung energized={energized} />
                </div>
                <div style={{
                  marginTop: 12, background: 'var(--panel2)', border: '1px solid var(--line)', borderRadius: 4, padding: '12px 14px', fontFamily: MONO, fontSize: 13.5, lineHeight: 1.7, color: 'var(--ink)', whiteSpace: 'pre', overflowX: 'auto',
                }}
                >
                  {/* The rung's SCL twin. The code itself never changes — only the
                    live values in the comment do, the way TIA shows them when
                    monitoring. An IF that assigned FALSE under "Start" read as
                    a bug to anyone who knows SCL. */}
                  <span style={{ color: '#5B7F5E' }}>(* Start *)</span>
                  {'\n'}
                  <span style={{ color: 'var(--cb)' }}>%Q0.0</span>
                  {' := '}
                  <span style={{ color: 'var(--cb)' }}>%I0.0</span>
                  ;
                  {'\n'}
                  <span style={{ color: '#5B7F5E' }}>
                    {'// %I0.0 = '}
                    <span style={{ color: energized ? 'var(--coil)' : 'var(--ink2)' }}>{energized ? 'TRUE' : 'FALSE'}</span>
                    {' → %Q0.0 = '}
                    <span style={{ color: energized ? 'var(--coil)' : 'var(--ink2)' }}>{energized ? 'TRUE' : 'FALSE'}</span>
                  </span>
                </div>
                {energized ? (
                  <div style={{
                    marginTop: 12, display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--coil)',
                  }}
                  >
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', background: 'var(--coil)',
                    }}
                    />
                    {UI.rungOn}
                  </div>
                ) : (
                  <div style={{
                    marginTop: 12, display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--ink3)',
                  }}
                  >
                    <span style={{
                      width: 7, height: 7, borderRadius: '50%', border: '1px solid var(--ink3)',
                    }}
                    />
                    {UI.rungOffBefore}
                    {' '}
                    <span style={{ color: 'var(--ink2)' }}>%I0.0</span>
                    {' '}
                    {UI.rungOffAfter}
                  </div>
                )}
                <div style={{
                  marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)', fontSize: 13, lineHeight: 1.5, color: 'var(--ink3)',
                }}
                >
                  {UI.rungCaption}
                  {' '}
                  <span style={{ color: 'var(--ink2)' }}>{UI.rungCaptionTail}</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* ===== TOUR ===== */}
        <section
          id="tour"
          data-cm-section="tour"
          className="cm-section"
          style={{ ...DARK_VARS, scrollMarginTop: 80, padding: '80px 0' }}
        >
          <div className="cm-pad" style={wrap}>
            <Eyebrow>{TOUR.eyebrow}</Eyebrow>
            <h2 style={{ ...h2, margin: '0 0 10px' }}>{TOUR.h2}</h2>
            <p style={{
              fontSize: 17, lineHeight: 1.6, color: 'var(--ink2)', margin: '0 0 26px', maxWidth: 720,
            }}
            >
              {TOUR.lead}
            </p>
            <HeroReel source={VIDEO.hero} ui={UI.media} />
          </div>
        </section>

        {/* ===== PAINS ===== */}
        <section
          data-cm-section="pains"
          className="cm-section"
          style={{ padding: '84px 0' }}
        >
          <div className="cm-pad" style={wrap}>
            <Eyebrow>{UI.painsEyebrow}</Eyebrow>
            <h2 style={{ ...h2, margin: '0 0 36px' }}>{UI.painsH2}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '34px 40px' }}>
              {PAINS.map((p) => (
                <div key={p.tag} style={{ borderTop: '1px solid var(--line2)', paddingTop: 18 }}>
                  <p style={{
                    fontSize: 18, lineHeight: 1.45, color: 'var(--ink)', margin: '0 0 12px', fontWeight: 500,
                  }}
                  >
                    {p.q}
                  </p>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--coil)' }}>{p.tag}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FEATURES ===== */}
        <section
          id="features"
          data-cm-section="features"
          className="cm-section"
          style={{ scrollMarginTop: 80, padding: '80px 0' }}
        >
          <div className="cm-pad" style={wrap}>
            <Eyebrow>{UI.featuresEyebrow}</Eyebrow>
            <h2 style={h2}>{UI.featuresH2}</h2>
            <p style={{
              fontSize: 17, color: 'var(--ink2)', margin: '0 0 34px', maxWidth: 620,
            }}
            >
              {FEATURES_LEAD}
            </p>
            <div
              className="cm-feat-grid"
              style={{
                display: 'grid', gridTemplateColumns: '300px 1fr', gap: 22, alignItems: 'start',
              }}
            >
              <div className="cm-feat-tabs" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {FEATURES.map((f, i) => {
                  const active = i === activeFeature;
                  return (
                    <button
                      key={f.name}
                      type="button"
                      onClick={() => {
                        setActiveFeature(i);
                        trackFeature(EN.FEATURES[i]?.name || f.name);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 11,
                        width: '100%',
                        textAlign: 'left',
                        padding: '13px 14px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 14.5,
                        fontWeight: active ? 600 : 500,
                        color: active ? 'var(--ink)' : 'var(--ink2)',
                        background: active ? accentA(10) : 'transparent',
                        border: '1px solid',
                        borderColor: active ? accentA(38) : 'transparent',
                        transition: 'background .15s, color .15s, border-color .15s',
                      }}
                    >
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', flex: 'none', background: active ? 'var(--coil)' : 'var(--line2)', boxShadow: 'none',
                      }}
                      />
                      <span style={{ flex: 1 }}>{f.name}</span>
                      {f.badge && (
                        <span style={{
                          fontSize: 12.5, color: 'var(--coil)', border: '1px solid var(--line2)', borderRadius: 5, padding: '2px 6px',
                        }}
                        >
                          {f.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {/* All panels share one grid cell, so the panel height is fixed
                  at the tallest one — switching tabs never reflows the page. */}
              <div style={{ display: 'grid' }}>
                {FEATURES.map((feat, i) => {
                  // Screenshots are keyed by the English tab name, so both
                  // languages pick up the same file.
                  const shot = SCREENSHOTS[EN.FEATURES[i]?.name];
                  const active = i === activeFeature;
                  return (
                    <div
                      key={feat.name}
                      aria-hidden={!active}
                      className="cm-feat-panel"
                      style={{
                        gridArea: '1 / 1',
                        visibility: active ? 'visible' : 'hidden',
                        background: 'var(--panel)',
                        border: '1px solid var(--line2)',
                        borderRadius: 8,
                        padding: 30,
                        minHeight: 380,
                      }}
                    >
                      {feat.badge && (
                        <span style={{
                          display: 'inline-block', fontSize: 12.5, color: 'var(--coil)', border: '1px solid color-mix(in srgb,var(--coil) 40%,transparent)', background: 'color-mix(in srgb,var(--coil) 10%,transparent)', borderRadius: 6, padding: '4px 9px', marginBottom: 16,
                        }}
                        >
                          {feat.badge}
                        </span>
                      )}
                      <h3 style={{
                        fontSize: 'clamp(24px,2.6vw,32px)', fontWeight: 600, letterSpacing: '-.01em', lineHeight: 1.12, margin: '0 0 16px',
                      }}
                      >
                        {feat.title}
                      </h3>
                      <p style={{
                        fontSize: 18, lineHeight: 1.6, color: 'var(--ink2)', margin: '0 0 22px', maxWidth: 640,
                      }}
                      >
                        {feat.value}
                      </p>
                      <div style={{
                        fontSize: 12, color: 'var(--coil)', letterSpacing: '.02em', marginBottom: 24,
                      }}
                      >
                        {feat.tag}
                      </div>
                      {feat.media && active && <LoopClip clip={feat.media} />}
                      {!feat.media && active && shot && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={shot.src}
                          alt={shot.alt}
                          width={640}
                          height={400}
                          loading="lazy"
                          style={{
                            display: 'block', width: '100%', maxWidth: 640, height: 'auto', aspectRatio: '16 / 10', objectFit: 'cover', borderRadius: 4, border: '1px solid var(--line)',
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* The tabs argue from benefit, which is right for a homepage. The
                sceptic who wants to know whether it handles tag tables gets a
                plain, verifiable list on its own page instead. */}
            <div style={{ marginTop: 22, fontSize: 15.5, color: 'var(--ink2)' }}>
              {`${CAPABILITIES_LINK.label} `}
              <MoreLink link={CAPABILITIES_LINK} location="features" />
            </div>
          </div>
        </section>

        {/* ===== PROOF — the full, unedited run =====
            The feature tabs only mean something if the capabilities were not a
            reel of lucky takes. This is the block that answers that, and it is
            the one thing on the page no competitor can copy cheaply. */}
        <section
          id="proof"
          data-cm-section="proof"
          className="cm-section"
          style={{
            ...DARK_VARS, scrollMarginTop: 80, padding: '84px 0',
          }}
        >
          <div className="cm-pad" style={wrap}>
            <Eyebrow>{PROOF.eyebrow}</Eyebrow>
            <h2 style={{ ...h2, maxWidth: 820 }}>{PROOF.h2}</h2>
            <p style={{
              fontSize: 17.5, lineHeight: 1.62, color: 'var(--ink2)', margin: '0 0 32px', maxWidth: 720,
            }}
            >
              {PROOF.lead}
            </p>
            <FullRun source={VIDEO.fullRun} chapters={FULL_RUN_CHAPTERS} ui={UI.media} />
            <p style={{
              marginTop: 26, paddingLeft: 18, borderLeft: `2px solid ${accentA(55)}`, fontSize: 18, lineHeight: 1.55, color: 'var(--ink)', maxWidth: 700, fontWeight: 500,
            }}
            >
              {PROOF.quote}
            </p>
          </div>
        </section>

        {/* ===== DIFFERENTIATORS ===== */}
        <section data-cm-section="different" className="cm-section" style={{ padding: '80px 0' }}>
          <div className="cm-pad" style={wrap}>
            <Eyebrow>{UI.diffEyebrow}</Eyebrow>
            <h2 style={{ ...h2, maxWidth: 760 }}>{UI.diffH2}</h2>
            <p style={{
              fontSize: 17, color: 'var(--ink2)', margin: '0 0 36px', maxWidth: 620,
            }}
            >
              {DIFF_LEAD}
            </p>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '30px 40px', marginBottom: 30,
            }}
            >
              {DIFFERENTIATORS.map((d) => (
                <div key={d.n} style={ruleTop}>
                  <h3 style={{
                    fontSize: 21, fontWeight: 600, letterSpacing: '-.01em', margin: '0 0 12px', lineHeight: 1.2,
                  }}
                  >
                    {d.title}
                  </h3>
                  <p style={{
                    fontSize: 15.5, lineHeight: 1.6, color: 'var(--ink2)', margin: 0,
                  }}
                  >
                    {d.body}
                  </p>
                </div>
              ))}
            </div>
            <p style={{
              margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink2)',
            }}
            >
              {DIFF_CHIPS.join(' · ')}
            </p>
          </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section
          data-cm-section="steps"
          className="cm-section"
          style={{
            padding: '80px 0', background: 'var(--bg2)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)',
          }}
        >
          <div className="cm-pad" style={wrap}>
            <Eyebrow>{UI.stepsEyebrow}</Eyebrow>
            <h2 style={{ ...h2, margin: '0 0 38px' }}>{UI.stepsH2}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '30px 40px' }}>
              {STEPS.map((s, i) => (
                <div key={s} style={ruleTop}>
                  <div style={{
                    fontSize: 30, fontWeight: 700, lineHeight: 1, color: 'var(--coil)', marginBottom: 14,
                  }}
                  >
                    {i + 1}
                  </div>
                  <p style={{
                    fontSize: 18, fontWeight: 500, lineHeight: 1.4, margin: 0, color: 'var(--ink)',
                  }}
                  >
                    {s}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SAFETY ===== */}
        <section
          id="safety"
          data-cm-section="safety"
          className="cm-section"
          style={{ scrollMarginTop: 80, padding: '80px 0' }}
        >
          <div className="cm-pad" style={wrap}>
            <div
              className="cm-safety"
              style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'start',
              }}
            >
              <div>
                <Eyebrow>{UI.safetyEyebrow}</Eyebrow>
                <h2 style={{
                  fontSize: 'clamp(26px,3vw,38px)', fontWeight: 600, letterSpacing: '-.01em', lineHeight: 1.12, margin: '0 0 20px',
                }}
                >
                  {UI.safetyH2}
                </h2>
                <p style={{
                  fontSize: 17, lineHeight: 1.62, color: 'var(--ink2)', margin: 0,
                }}
                >
                  {SAFETY_LEAD}
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {SAFETY_BULLETS.map((b) => (
                  <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                    <span style={{
                      flex: 'none', width: 24, height: 24, borderRadius: 7, background: 'color-mix(in srgb,var(--coil) 15%,transparent)', border: '1px solid color-mix(in srgb,var(--coil) 45%,transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--coil)', fontSize: 13,
                    }}
                    >
                      ✓
                    </span>
                    <span style={{ fontSize: 16, color: 'var(--ink)' }}>{b}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* The animated twin of the terminal this section used to hand-draw:
                same snapshot → failed build → auto-rollback, played rather than
                printed, and at the width its type was drawn for. It says no
                word the copy above it does not. */}
            <LoopClip
              clip={CLIPS.safety}
              style={{ marginTop: 34 }}
            />
          </div>
        </section>

        {/* ===== WHO ===== */}
        <section
          data-cm-section="who"
          className="cm-section"
          style={{
            padding: '80px 0', background: 'var(--bg2)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)',
          }}
        >
          <div className="cm-pad" style={wrap}>
            <Eyebrow>{UI.whoEyebrow}</Eyebrow>
            <h2 style={{ ...h2, margin: '0 0 18px', maxWidth: 760 }}>
              {UI.whoH2}
            </h2>
            <p style={{
              fontSize: 18, lineHeight: 1.62, color: 'var(--ink2)', margin: '0 0 32px', maxWidth: 760,
            }}
            >
              {WHO.lead}
            </p>
            <div
              className="cm-who"
              style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px 40px', marginBottom: 28,
              }}
            >
              <div style={ruleTop}>
                <div style={{
                  fontSize: 13.5, fontWeight: 600, color: 'var(--coil)', marginBottom: 10,
                }}
                >
                  {UI.solo}
                </div>
                <p style={{
                  fontSize: 17, lineHeight: 1.5, margin: 0, color: 'var(--ink)',
                }}
                >
                  {WHO.solo}
                </p>
              </div>
              <div style={ruleTop}>
                <div style={{
                  fontSize: 13.5, fontWeight: 600, color: 'var(--coil)', marginBottom: 10,
                }}
                >
                  {UI.team}
                </div>
                <p style={{
                  fontSize: 17, lineHeight: 1.5, margin: 0, color: 'var(--ink)',
                }}
                >
                  {WHO.team}
                </p>
              </div>
            </div>
            <p style={{
              margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink2)',
            }}
            >
              {WHO_CHIPS.map((c) => c.t).join(' · ')}
            </p>
          </div>
        </section>

        {/* ===== WHO'S BEHIND THIS =====
            Deliberately placed immediately before pricing: it has to be read in
            the moment the visitor decides whether to hand over money. One
            engineer with a name and an inbox is the one thing Siemens cannot
            put on their page. */}
        <section data-cm-section="founder" className="cm-section" style={{ padding: '84px 0' }}>
          <div className="cm-pad" style={wrap}>
            <Eyebrow>{FOUNDER.eyebrow}</Eyebrow>
            <div
              className="cm-founder"
              style={{
                display: 'grid', gridTemplateColumns: PHOTOS.founder ? '260px minmax(0,1fr)' : '1fr', gap: 36, alignItems: 'start', maxWidth: PHOTOS.founder ? 1000 : 780,
              }}
            >
              {PHOTOS.founder && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={PHOTOS.founder.src}
                  alt={PHOTOS.founder.alt}
                  width={260}
                  height={325}
                  loading="lazy"
                  style={{
                    display: 'block', width: '100%', height: 'auto', aspectRatio: '4 / 5', objectFit: 'cover', borderRadius: 4, border: '1px solid var(--line)',
                  }}
                />
              )}
              <div>
                <h2 style={{
                  fontSize: 'clamp(26px,3vw,36px)', fontWeight: 600, letterSpacing: '-.01em', lineHeight: 1.14, margin: '0 0 20px',
                }}
                >
                  {FOUNDER.h2}
                </h2>
                {FOUNDER.paragraphs.map((p) => (
                  <p
                    key={p.slice(0, 40)}
                    style={{
                      fontSize: 17, lineHeight: 1.68, color: 'var(--ink2)', margin: '0 0 16px',
                    }}
                  >
                    {p}
                  </p>
                ))}
                <div style={{
                  marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--line)', fontSize: 13.5, color: 'var(--ink)',
                }}
                >
                  {FOUNDER.signature}
                  <br />
                  <a href={`mailto:${FOUNDER.email}`} style={{ color: 'var(--coil)', textDecoration: 'none' }}>
                    {FOUNDER.email}
                  </a>
                </div>
              </div>
            </div>

            {PHOTOS.workbench && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={PHOTOS.workbench.src}
                alt={PHOTOS.workbench.alt}
                width={1000}
                height={667}
                loading="lazy"
                style={{
                  display: 'block', marginTop: 28, width: '100%', maxWidth: 1000, height: 'auto', aspectRatio: '3 / 2', objectFit: 'cover', borderRadius: 4, border: '1px solid var(--line)',
                }}
              />
            )}

            {/* Renders only once real figures land in content.js — the copy deck
                is explicit that invented numbers get caught in the first call. */}
            {BRAND_STATS.length > 0 && (
              <div
                className="cm-stats"
                style={{
                  marginTop: 22, maxWidth: 780, display: 'grid', gridTemplateColumns: `repeat(${BRAND_STATS.length},1fr)`, gap: 16,
                }}
              >
                {BRAND_STATS.map((s) => (
                  <div
                    key={s.label}
                    style={{
                      background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 8, padding: '20px 22px',
                    }}
                  >
                    <div style={{
                      fontSize: 26, fontWeight: 600, color: 'var(--coil)', marginBottom: 8,
                    }}
                    >
                      {s.value}
                    </div>
                    <div style={{
                      fontSize: 13.5, lineHeight: 1.45, color: 'var(--ink2)', whiteSpace: 'pre-line',
                    }}
                    >
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ===== PRICING ===== */}
        <section
          id="pricing"
          data-cm-section="pricing"
          className="cm-section"
          style={{
            scrollMarginTop: 80, padding: '80px 0', background: 'var(--bg2)', borderTop: '1px solid var(--line)',
          }}
        >
          <div className="cm-pad" style={wrap}>
            <Eyebrow>{UI.pricingEyebrow}</Eyebrow>
            <h2 style={{ ...h2, margin: '0 0 10px' }}>{UI.pricingH2}</h2>
            <p style={{
              fontSize: 17, color: 'var(--ink2)', margin: '0 0 26px', maxWidth: 640,
            }}
            >
              {UI.pricingLead}
            </p>

            {/* Monthly stays first and stays the default: paying month to month
                is the argument against an annual-only competitor, so hiding it
                behind the discounted option would trade away the point. */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, marginBottom: 28,
            }}
            >
              <div style={{
                display: 'inline-flex', padding: 4, gap: 4, background: 'var(--panel)', border: '1px solid var(--line2)', borderRadius: 6,
              }}
              >
                {[['month', UI.monthly], ['year', UI.yearly]].map(([key, label]) => {
                  const on = billing === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => { setBilling(key); trackBilling(key); }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '9px 16px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 14.5,
                        fontWeight: on ? 600 : 500,
                        color: on ? '#FFFFFF' : 'var(--ink2)',
                        background: on ? 'var(--coil)' : 'transparent',
                        border: 'none',
                      }}
                    >
                      {label}
                      {key === 'year' && (
                        <span style={{
                          fontSize: 12.5, color: on ? '#FFFFFF' : 'var(--coil)', border: `1px solid ${on ? 'rgba(255,255,255,.55)' : accentA(40)}`, borderRadius: 5, padding: '2px 6px',
                        }}
                        >
                          {yearlySavingPercent(plans[0]) != null
                            ? UI.save(yearlySavingPercent(plans[0]))
                            : UI.twoMonthsFree}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <span style={{ fontSize: 13.5, color: 'var(--ink3)' }}>{BILLING_NOTE}</span>
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))', gap: 16,
            }}
            >
              {plans.map((plan) => {
                const rec = plan.recommended;
                const sale = saleFor(plan, billing);
                return (
                  <div
                    key={plan.key}
                    style={{
                      position: 'relative',
                      background: rec
                        ? 'var(--panel)'
                        : 'var(--panel)',
                      border: rec ? '1px solid color-mix(in srgb,var(--coil) 55%,transparent)' : '1px solid var(--line2)',
                      borderRadius: 8,
                      padding: '26px 22px 24px',
                      boxShadow: 'none',
                    }}
                  >
                    {rec && (
                      <span style={{
                        position: 'absolute', top: -11, left: 22, fontSize: 12.5, color: '#FFFFFF', fontWeight: 700, background: 'var(--coil)', borderRadius: 6, padding: '4px 9px',
                      }}
                      >
                        {UI.recommended}
                      </span>
                    )}
                    <div style={{
                      fontSize: 17, fontWeight: 600, letterSpacing: '-.01em', color: 'var(--ink)',
                    }}
                    >
                      {plan.label}
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'baseline', gap: 5, margin: '12px 0 4px',
                    }}
                    >
                      {/* A struck-through price is shown only when the server
                          says the plan is on sale, at the figure the server
                          gives. The frontend does no discount arithmetic. */}
                      {sale && (
                        <span style={{
                          fontSize: 19, color: 'var(--ink3)', textDecoration: 'line-through',
                        }}
                        >
                          {UI.money(CURRENCY, sale.was.toLocaleString(UI.numberLocale))}
                        </span>
                      )}
                      <span style={{
                        fontSize: 30, fontWeight: 600, color: 'var(--ink)',
                      }}
                      >
                        {UI.money(
                          CURRENCY,
                          priceFor(plan, billing).toLocaleString(UI.numberLocale),
                        )}
                      </span>
                      <span style={{ fontSize: 13.5, color: 'var(--ink2)' }}>
                        {yearly ? UI.perYear : UI.perMonth(PERIOD)}
                      </span>
                      {sale?.percent != null && (
                        <span style={{
                          fontSize: 12.5, color: '#FFFFFF', fontWeight: 700, background: 'var(--coil)', borderRadius: 5, padding: '3px 7px',
                        }}
                        >
                          {`−${sale.percent}%`}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13.5, color: 'var(--ink3)', marginBottom: 18 }}>
                      {yearly
                        ? UI.licenceLineYear(UI.money(CURRENCY, yearlySaving(plan)))
                        : UI.licenceLine}
                    </div>

                    <div style={{
                      display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 20,
                    }}
                    >
                      {[
                        UI.tasksLine(plan.tasksPerMonth.toLocaleString(UI.numberLocale)),
                        UI.devicesLine(plan.maxDevices),
                        UI.reqLine(plan.reqPerMin),
                      ].map((line) => (
                        <div
                          key={line}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, fontSize: 14.5, color: 'var(--ink)',
                          }}
                        >
                          <span style={{
                            flex: 'none', width: 18, height: 18, borderRadius: 5, background: accentA(15), border: `1px solid ${accentA(45)}`, color: 'var(--coil)', fontSize: 11, lineHeight: '16px', textAlign: 'center',
                          }}
                          >
                            ✓
                          </span>
                          {line}
                        </div>
                      ))}
                    </div>

                    <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 18 }}>
                      <span style={{ color: 'var(--ink3)' }}>{UI.bestFor}</span>
                      {UI.planBestFor?.[plan.key] ?? plan.bestFor.toLowerCase()}
                    </div>

                    <TrialCta
                      location="pricing_card"
                      label={UI.planTrial}
                      href={trialHref}
                      variant={rec ? 'primary' : 'ghost'}
                      extra={{ plan_key: plan.key, billing_period: billing }}
                      style={{
                        display: 'block',
                        textAlign: 'center',
                        fontSize: 14.5,
                        padding: '11px 16px',
                        borderRadius: 6,
                        boxShadow: 'none',
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* What a "task" is, and the honest shape of the allowance. This is
                the difference from BYO-token competitors, so it belongs in the
                price block rather than buried in the FAQ. */}
            <p style={{
              marginTop: 20, fontSize: 14.5, lineHeight: 1.62, color: 'var(--ink2)', maxWidth: 820,
            }}
            >
              {PRICING_NOTE}
            </p>

            {/* A licence is machines, not named people — and unlike a Siemens
                seat, two of you may work at once. Stated here because the page
                previously said "per engineer" and gave the advantage away. */}
            <div style={{
              marginTop: 18, padding: '16px 18px', border: `1px solid ${accentA(28)}`, background: accentA(7), borderRadius: 8, fontSize: 15, lineHeight: 1.6, color: 'var(--ink)', maxWidth: 820,
            }}
            >
              {LICENCE_NOTE}
            </div>

            <div style={{
              marginTop: 16, fontSize: 14.5, color: 'var(--ink2)', borderTop: '1px solid var(--line)', paddingTop: 16,
            }}
            >
              {LANGUAGE_LINE}
            </div>

            <div style={{
              marginTop: 26, display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center',
            }}
            >
              <TrialCta
                location="pricing_footer"
                label={TRIAL.cta}
                href={trialHref}
                extra={{ billing_period: billing }}
                style={{ fontSize: 16, padding: '14px 24px', borderRadius: 6 }}
              />
              <span style={{ fontSize: 13.5, color: 'var(--ink3)' }}>{TRIAL.micro}</span>
            </div>
            <div style={{
              marginTop: 14, fontSize: 12, color: 'var(--ink3)',
            }}
            >
              {CHECKOUT_NOTE}
            </div>
          </div>
        </section>

        {/* ===== BEFORE YOU DECIDE — the sub-pages ===== */}
        <section data-cm-section="decide" className="cm-section" style={{ padding: '80px 0 0' }}>
          <div className="cm-pad" style={wrap}>
            <Eyebrow>{DECIDE.eyebrow}</Eyebrow>
            <h2 style={{ ...h2, margin: '0 0 28px' }}>{DECIDE.h2}</h2>
            <div
              className="cm-who"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}
            >
              {DECIDE.cards.map((card) => (
                <a
                  key={card.href}
                  href={card.href}
                  onClick={() => trackLink('decide', card.href)}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 10, padding: 24, borderRadius: 8, textDecoration: 'none', background: 'var(--panel)', border: '1px solid var(--line2)',
                  }}
                >
                  <span style={{ fontSize: 19, fontWeight: 600, color: 'var(--ink)' }}>{card.title}</span>
                  <span style={{
                    fontSize: 15, lineHeight: 1.55, color: 'var(--ink2)', flex: 1,
                  }}
                  >
                    {card.body}
                  </span>
                  <span style={{ fontWeight: 500, color: 'var(--coil)' }}>{`${card.more} →`}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section
          id="faq"
          data-cm-section="faq"
          className="cm-section"
          style={{ scrollMarginTop: 80, padding: '80px 0' }}
        >
          <div className="cm-pad" style={{ maxWidth: 880, margin: '0 auto', padding: '0 28px' }}>
            <Eyebrow>{UI.faqEyebrow}</Eyebrow>
            <h2 style={{ ...h2, margin: '0 0 32px' }}>{UI.faqH2}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Native <details> rather than a React accordion: every answer is
                  in the server HTML, where a crawler reads it, instead of being
                  mounted only after a click. `name` keeps one open at a time. */}
              {FAQS.map((item, i) => (
                <details
                  key={item.q}
                  name="cm-faq"
                  className="cm-faq"
                  open={i === 0}
                  onToggle={(e) => { if (e.currentTarget.open) trackFaq(item.q); }}
                  style={{
                    background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden',
                  }}
                >
                  <summary
                    style={{
                      display: 'flex', alignItems: 'center', gap: 16, padding: '19px 22px', cursor: 'pointer', color: 'var(--ink)', fontSize: 16.5, fontWeight: 500,
                    }}
                  >
                    <span style={{ flex: 1 }}>{item.q}</span>
                    <span
                      className="cm-faq-plus"
                      aria-hidden="true"
                      style={{
                        flex: 'none', fontSize: 20, fontWeight: 300, lineHeight: 1,
                      }}
                    >
                      +
                    </span>
                  </summary>
                  <div style={{
                    padding: '0 22px 20px', fontSize: 15.5, lineHeight: 1.62, color: 'var(--ink2)',
                  }}
                  >
                    {item.a.split('\n\n').map((para) => (
                      <p key={para.slice(0, 40)} style={{ margin: '0 0 12px' }}>{para}</p>
                    ))}
                    <MoreLink link={item} location={`faq:${item.q.slice(0, 40)}`} />
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <Footer
          lang={lang}
          tagline={FOOTER.tagline}
          by={FOOTER.by}
          specs={FOOTER.specs}
          disclaimer={FOOTER.disclaimer}
        />
      </>
    </Shell>
  );
}
